import { useRef, useState } from "react";
import { FileIcon, Trash2, Upload } from "lucide-react";

import {
  useDeleteAttachment,
  type DeleteAttachmentVariables,
} from "../../api/hooks/useDeleteAttachment";
import { useTradeAttachments } from "../../api/hooks/useTradeAttachments";
import {
  useUploadAttachment,
  type UploadAttachmentVariables,
} from "../../api/hooks/useUploadAttachment";
import { useAttachmentBlobUrl } from "../../api/hooks/useAttachmentBlobUrl";
import type { components } from "../../api/schema";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";

// Server-side cap shipped in workspaces/infrastructure/config/settings.py
// (Settings.attachment_max_size_bytes default = 25 * 1024 * 1024). Pre-flight
// here gives the user inline feedback before the 413 round-trip.
export const ATTACHMENT_MAX_SIZE_BYTES = 25 * 1024 * 1024;

type Attachment = components["schemas"]["AttachmentResponse"];

type AttachmentGalleryProps = {
  tradeId: string;
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function AttachmentGallery({ tradeId }: AttachmentGalleryProps) {
  const { data, isLoading, isError } = useTradeAttachments(tradeId);
  const uploadMutation = useUploadAttachment();
  const deleteMutation = useDeleteAttachment();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(
    null,
  );
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function handleDrop(event: React.DragEvent<HTMLLabelElement>): void {
    event.preventDefault();
    setDragging(false);
    const dropped = Array.from(event.dataTransfer.files);
    if (dropped.length > 0) setSelectedFiles(dropped);
  }

  const oversizedCount = selectedFiles.filter(
    (file) => file.size > ATTACHMENT_MAX_SIZE_BYTES,
  ).length;

  function handleFilesChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const list = event.target.files;
    if (list === null) {
      setSelectedFiles([]);
      return;
    }
    setSelectedFiles(Array.from(list));
  }

  function clearSelection(): void {
    setSelectedFiles([]);
    if (fileInputRef.current !== null) {
      fileInputRef.current.value = "";
    }
  }

  async function handleUpload(): Promise<void> {
    if (selectedFiles.length === 0 || oversizedCount > 0) return;
    for (const file of selectedFiles) {
      const variables: UploadAttachmentVariables = { tradeId, file };
      await uploadMutation.mutateAsync(variables);
    }
    clearSelection();
  }

  function handleDelete(attachment: Attachment): void {
    const variables: DeleteAttachmentVariables = {
      attachmentId: attachment.attachment_id,
      tradeId,
    };
    deleteMutation.mutate(variables);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Attachments</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <label
            htmlFor="attachment-file-input"
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            className={`flex cursor-pointer items-center gap-4 rounded-card border border-dashed px-5 py-6 transition-colors ${
              dragging
                ? "border-primary bg-primary/5"
                : "border-outline bg-surface-container-high hover:border-primary"
            }`}
          >
            <Upload className="h-7 w-7 shrink-0 text-primary" aria-hidden="true" />
            <div>
              <p className="text-sm font-semibold text-on-surface">
                Drag screenshots here
              </p>
              <p className="text-xs text-on-surface-muted">
                or <span className="font-medium text-on-surface-variant">browse files</span>{" "}
                · PNG, JPG, PDF up to {formatBytes(ATTACHMENT_MAX_SIZE_BYTES)}
              </p>
            </div>
          </label>
          <input
            ref={fileInputRef}
            id="attachment-file-input"
            type="file"
            multiple
            aria-label="Add files"
            onChange={handleFilesChange}
            className="sr-only"
          />
          {selectedFiles.length > 0 && (
            <ul className="space-y-1 text-sm">
              {selectedFiles.map((file) => {
                const oversized = file.size > ATTACHMENT_MAX_SIZE_BYTES;
                return (
                  <li
                    key={`${file.name}-${file.size}-${file.lastModified}`}
                    className="flex items-center justify-between gap-3 rounded-control bg-surface-container px-3 py-1.5"
                  >
                    <span className="truncate text-on-surface">{file.name}</span>
                    <span
                      className={
                        oversized
                          ? "text-xs font-medium text-danger"
                          : "text-xs text-on-surface-variant"
                      }
                    >
                      {formatBytes(file.size)}
                      {oversized ? " — exceeds 25 MB" : ""}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          {oversizedCount > 0 && (
            <p role="alert" className="text-sm text-danger">
              {oversizedCount === 1
                ? "Remove the file larger than 25 MB to continue."
                : `Remove the ${oversizedCount} files larger than 25 MB to continue.`}
            </p>
          )}
          {uploadMutation.isError && (
            <p role="alert" className="text-sm text-danger">
              {uploadMutation.error?.message ?? "Upload failed."}
            </p>
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={() => {
                void handleUpload();
              }}
              disabled={
                selectedFiles.length === 0 ||
                oversizedCount > 0 ||
                uploadMutation.isPending
              }
            >
              {uploadMutation.isPending
                ? "Uploading…"
                : `Upload${selectedFiles.length > 0 ? ` (${selectedFiles.length})` : ""}`}
            </Button>
            {selectedFiles.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                onClick={clearSelection}
                disabled={uploadMutation.isPending}
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-medium text-on-surface-variant">
            Existing attachments
          </h3>
          {isLoading ? (
            <p
              role="status"
              aria-live="polite"
              className="text-sm text-on-surface-variant"
            >
              Loading attachments…
            </p>
          ) : isError ? (
            <p role="alert" className="text-sm text-danger">
              Failed to load attachments.
            </p>
          ) : data === undefined || data.items.length === 0 ? (
            <p className="text-sm text-on-surface-variant">
              No attachments yet.
            </p>
          ) : (
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {data.items.map((attachment) => (
                <AttachmentTile
                  key={attachment.attachment_id}
                  attachment={attachment}
                  onPreview={setPreviewAttachment}
                  onDelete={handleDelete}
                  deleting={
                    deleteMutation.isPending &&
                    deleteMutation.variables?.attachmentId ===
                      attachment.attachment_id
                  }
                />
              ))}
            </ul>
          )}
        </div>
      </CardContent>

      <Dialog
        open={previewAttachment !== null}
        onOpenChange={(open) => {
          if (!open) setPreviewAttachment(null);
        }}
      >
        <DialogContent className="max-w-4xl">
          <DialogTitle>{previewAttachment?.filename ?? "Preview"}</DialogTitle>
          {previewAttachment !== null && (
            <AttachmentPreview attachment={previewAttachment} />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

type AttachmentTileProps = {
  attachment: Attachment;
  onPreview: (attachment: Attachment) => void;
  onDelete: (attachment: Attachment) => void;
  deleting: boolean;
};

function AttachmentTile({
  attachment,
  onPreview,
  onDelete,
  deleting,
}: AttachmentTileProps) {
  const isImage = attachment.content_type.startsWith("image/");
  return (
    <li className="flex flex-col gap-2 rounded-card border border-outline-variant bg-surface-container p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-medium text-on-surface" title={attachment.filename}>
          {attachment.filename}
        </span>
        <button
          type="button"
          aria-label={`Delete ${attachment.filename}`}
          onClick={() => onDelete(attachment)}
          disabled={deleting}
          className="rounded-control p-1 text-on-surface-variant hover:bg-surface-container-high hover:text-danger disabled:opacity-50"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      {isImage ? (
        <AttachmentThumbnail
          attachment={attachment}
          onClick={() => onPreview(attachment)}
        />
      ) : (
        <div className="flex h-20 items-center justify-center rounded-control bg-surface-container-high text-on-surface-variant">
          <FileIcon className="h-8 w-8" />
        </div>
      )}
      <div className="flex items-center justify-between text-xs text-on-surface-variant">
        <span>{formatBytes(attachment.size_bytes)}</span>
        <span className="font-mono">{attachment.content_type}</span>
      </div>
    </li>
  );
}

type AttachmentThumbnailProps = {
  attachment: Attachment;
  onClick: () => void;
};

function AttachmentThumbnail({ attachment, onClick }: AttachmentThumbnailProps) {
  const { data: blobUrl, isLoading, isError } = useAttachmentBlobUrl(
    attachment.attachment_id,
    true,
  );
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Open ${attachment.filename}`}
      className="flex h-20 w-full items-center justify-center overflow-hidden rounded-control bg-surface-container-high focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
    >
      {isLoading ? (
        <span className="text-xs text-on-surface-variant">Loading…</span>
      ) : isError || blobUrl === undefined ? (
        <span className="text-xs text-danger">Preview unavailable</span>
      ) : (
        <img
          src={blobUrl}
          alt={attachment.filename}
          className="h-full w-full object-cover"
        />
      )}
    </button>
  );
}

type AttachmentPreviewProps = {
  attachment: Attachment;
};

function AttachmentPreview({ attachment }: AttachmentPreviewProps) {
  const isImage = attachment.content_type.startsWith("image/");
  const { data: blobUrl, isLoading, isError } = useAttachmentBlobUrl(
    attachment.attachment_id,
    isImage,
  );
  if (!isImage) {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-on-surface-variant">
        <FileIcon className="h-12 w-12" />
        <p className="text-sm">
          Preview is only available for image attachments.
        </p>
        <p className="text-xs font-mono">{attachment.content_type}</p>
      </div>
    );
  }
  if (isLoading) {
    return (
      <p role="status" className="py-8 text-center text-on-surface-variant">
        Loading preview…
      </p>
    );
  }
  if (isError || blobUrl === undefined) {
    return (
      <p role="alert" className="py-8 text-center text-danger">
        Failed to load preview.
      </p>
    );
  }
  return (
    <img
      src={blobUrl}
      alt={attachment.filename}
      className="max-h-[70vh] w-auto self-center"
    />
  );
}
